import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Search,
  Package,
  FileText,
  MessageSquare,
  User,
  Star,
  TrendingUp,
  Database,
  ChevronLeft,
  ChevronRight,
  Eye,
  ArrowLeft
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';
import { toast } from '@/components/ui/use-toast';

interface CategoryStat {
  source_type: string;
  count: number;
  total_usage: number;
  avg_priority: number;
  last_added: string;
}

interface KnowledgeEntry {
  id: string;
  title: string;
  content: string;
  source_type: string;
  priority: number;
  approval_status: string;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  created_by_name: string | null;
  created_by_email: string | null;
  metadata: any;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const SOURCE_TYPE_ICONS: Record<string, any> = {
  product_feed: Package,
  technical_review: FileText,
  reviews: MessageSquare,
  generated_answer: Star,
  expert_interview: User,
  manual: FileText
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  product_feed: 'Product Feed',
  technical_review: 'Technical Reviews',
  reviews: 'Customer Reviews',
  manual: 'Manual Entries',
  expert_interview: 'Expert Interviews',
  generated_answer: 'AI Generated Answers'
};

const SOURCE_TYPE_DESCRIPTIONS: Record<string, string> = {
  product_feed: 'Product catalog and specifications',
  technical_review: 'Technical documentation and guides',
  reviews: 'Customer feedback and reviews',
  manual: 'Manually uploaded documents',
  expert_interview: 'Expert knowledge and interviews',
  generated_answer: 'AI-generated Q&A content'
};

// Define which source types are used by chatbot
const CHATBOT_SOURCES = new Set(['product_feed', 'technical_review', 'manual', 'expert_interview', 'generated_answer']);

// Define usage labels for each source type
const USAGE_LABELS: Record<string, string> = {
  product_feed: 'Chatbot & Research',
  technical_review: 'Chatbot & Research',
  reviews: 'Research Only',
  manual: 'Chatbot & Research',
  expert_interview: 'Chatbot & Research',
  generated_answer: 'Chatbot & Research'
};

export default function KnowledgeBase() {
  const { selectedSite } = useSite();

  const [stats, setStats] = useState<CategoryStat[]>([]);
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    if (selectedSite?.id) {
      fetchStats();
    }
  }, [selectedSite]);

  useEffect(() => {
    if (selectedSite?.id && (selectedCategory || searchQuery)) {
      fetchEntries();
    }
  }, [selectedSite, selectedCategory, pagination.page, searchQuery]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`/api/answer-generation/knowledge-base/stats?siteId=${selectedSite?.id}`);
      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data.stats || []);
    } catch (err) {
      console.error('Error fetching stats:', err);
      toast({
        title: "Error",
        description: "Failed to load knowledge base statistics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      let url = `/api/answer-generation/knowledge-base?siteId=${selectedSite?.id}&approvalStatus=approved&page=${pagination.page}&limit=${pagination.limit}`;

      if (selectedCategory) {
        url += `&sourceType=${selectedCategory}`;
      }

      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      const response = await authFetch(url);
      if (!response.ok) throw new Error('Failed to fetch entries');

      const data = await response.json();
      setEntries(data.entries || []);
      if (data.pagination) {
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('Error fetching entries:', err);
      toast({
        title: "Error",
        description: "Failed to load knowledge base entries",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (sourceType: string) => {
    setSelectedCategory(sourceType);
    setPagination({ ...pagination, page: 1 });
  };

  const handleBack = () => {
    setSelectedCategory(null);
    setSearchQuery('');
    setEntries([]);
    setPagination({ ...pagination, page: 1 });
  };

  const handleViewDetails = async (entry: KnowledgeEntry) => {
    try {
      const response = await authFetch(`/api/answer-generation/knowledge-base/${entry.id}`);
      if (!response.ok) throw new Error('Failed to fetch entry details');

      const data = await response.json();
      setSelectedEntry(data.entry);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error fetching entry details:', err);
      toast({
        title: "Error",
        description: "Failed to load entry details",
        variant: "destructive"
      });
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setSelectedCategory(null); // Search across all categories
    setPagination({ ...pagination, page: 1 });
  };

  const SourceIcon = ({ type }: { type: string }) => {
    const Icon = SOURCE_TYPE_ICONS[type] || FileText;
    return <Icon className="w-6 h-6" />;
  };

  // Category Cards View
  const renderCategoryCards = () => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {stats.map((stat) => {
        const Icon = SOURCE_TYPE_ICONS[stat.source_type] || FileText;
        return (
          <Card
            key={stat.source_type}
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleCategoryClick(stat.source_type)}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {SOURCE_TYPE_LABELS[stat.source_type] || stat.source_type}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {SOURCE_TYPE_DESCRIPTIONS[stat.source_type]}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Entries</span>
                  <span className="text-2xl font-bold">{stat.count.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Uses</span>
                  <span className="text-lg font-semibold">{parseInt(stat.total_usage).toLocaleString()}</span>
                </div>
                <Badge variant={CHATBOT_SOURCES.has(stat.source_type) ? 'default' : 'secondary'} className="w-full justify-center">
                  {USAGE_LABELS[stat.source_type] || 'Unknown'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  // Entries List View
  const renderEntriesList = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Categories
        </Button>
        <p className="text-sm text-muted-foreground">
          Showing {entries.length} of {pagination.total.toLocaleString()} entries
        </p>
      </div>

      {entries.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">
              {searchQuery ? 'No entries match your search' : 'No entries found in this category'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {entries.map((entry) => (
            <Card key={entry.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6 space-y-3">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 flex-wrap flex-1">
                    <Badge variant="outline" className="flex items-center gap-1">
                      <SourceIcon type={entry.source_type} />
                      {SOURCE_TYPE_LABELS[entry.source_type] || entry.source_type}
                    </Badge>
                    {entry.usage_count > 0 && (
                      <Badge variant="secondary">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        Used {entry.usage_count}x
                      </Badge>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleViewDetails(entry)}>
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                </div>

                {/* Title */}
                <div>
                  <h3 className="font-semibold text-lg line-clamp-1">{entry.title}</h3>
                </div>

                {/* Content Preview */}
                <div>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {entry.content}
                  </p>
                </div>

                {/* Metadata */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  {entry.created_by_name && (
                    <span>Added by {entry.created_by_name}</span>
                  )}
                  <span>Created {new Date(entry.created_at).toLocaleDateString()}</span>
                  {entry.last_used_at && (
                    <span>Last used {new Date(entry.last_used_at).toLocaleDateString()}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page === pagination.totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Organization Knowledge</h1>
        <p className="text-muted-foreground">
          All uploaded content for your organization - product feeds, documents, reviews, and more
        </p>
      </div>

      {/* Search Bar - Always visible */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search across all categories..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading knowledge base...</p>
          </CardContent>
        </Card>
      ) : selectedCategory || searchQuery ? (
        renderEntriesList()
      ) : stats.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">No uploaded content yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Upload product feeds, documents, or reviews to build your organization's knowledge
            </p>
          </CardContent>
        </Card>
      ) : (
        renderCategoryCards()
      )}

      {/* Document Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          {selectedEntry && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">{selectedEntry.title}</DialogTitle>
                <DialogDescription>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="flex items-center gap-1">
                      <SourceIcon type={selectedEntry.source_type} />
                      {SOURCE_TYPE_LABELS[selectedEntry.source_type]}
                    </Badge>
                    <Badge variant={CHATBOT_SOURCES.has(selectedEntry.source_type) ? 'default' : 'secondary'}>
                      {USAGE_LABELS[selectedEntry.source_type]}
                    </Badge>
                    {selectedEntry.usage_count > 0 && (
                      <Badge variant="secondary">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        Used {selectedEntry.usage_count}x
                      </Badge>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Content */}
                <div className="border rounded-lg p-4 bg-muted/50">
                  <h3 className="font-semibold mb-2">Content</h3>
                  <div className="prose prose-sm max-w-none">
                    <p className="whitespace-pre-wrap">{selectedEntry.content}</p>
                  </div>
                </div>

                {/* Metadata */}
                {selectedEntry.metadata && Object.keys(selectedEntry.metadata).length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-2">Metadata</h3>
                    <dl className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(selectedEntry.metadata).map(([key, value]) => (
                        <div key={key}>
                          <dt className="text-muted-foreground">{key}:</dt>
                          <dd className="font-medium">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                {/* Info */}
                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Created by:</span>
                      <p className="font-medium">{selectedEntry.created_by_name || 'Unknown'}</p>
                      {selectedEntry.created_by_email && (
                        <p className="text-xs text-muted-foreground">{selectedEntry.created_by_email}</p>
                      )}
                    </div>
                    <div>
                      <span className="text-muted-foreground">Created at:</span>
                      <p className="font-medium">{new Date(selectedEntry.created_at).toLocaleString()}</p>
                    </div>
                    {selectedEntry.last_used_at && (
                      <div>
                        <span className="text-muted-foreground">Last used:</span>
                        <p className="font-medium">{new Date(selectedEntry.last_used_at).toLocaleString()}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Priority:</span>
                      <p className="font-medium">{selectedEntry.priority}</p>
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
